import { Box, Breadcrumbs, Typography, Link, Chip, Stack } from "@mui/material"
import { Link as RouterLink, useMatch } from "react-router-dom"
import React from "react"
import { ellipsisText } from "../utils/text"

type BreadcrumbsProps = {
  label: string
  iconType?: React.ElementType
  to?: string
}

type ChipProps = {
  label: string
  iconType: React.ElementType
  to: string
  matchTo?: string
}

type PageTitleProps = {
  title: string
  breadcrumbs?: BreadcrumbsProps[]
  padding?: boolean
  border?: boolean
  tabs?: ChipProps[]
  rightBlock?: React.ReactNode
}

function TBreadcrumb({ label, iconType, to }: BreadcrumbsProps) {
  return (
    <Link
      component={to ? RouterLink : Typography}
      to={to}
      color={to ? "text.primary" : "primary.main"}
      underline={to ? "hover" : "none"}
      sx={{
        cursor: to ? "pointer" : "default",
      }}
    >
      <Typography
        component={"span"}
        noWrap
        sx={{
          display: "flex",
          alignItems: "center",
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {iconType &&
          React.createElement(iconType, {
            sx: { mr: 0.5 },
            fontSize: "inherit",
          })}
        {ellipsisText(label, 100)}
      </Typography>
    </Link>
  )
}

function TChip({ label, iconType, to, matchTo }: ChipProps) {
  const icon =
    iconType &&
    React.createElement(iconType, {
      sx: { mr: 0.5 },
      fontSize: "inherit",
    })
  const match = useMatch(matchTo ?? to)

  return (
    <Chip
      icon={icon}
      label={label}
      component={RouterLink}
      to={to}
      color="primary"
      sx={{
        mr: 1,
        textTransform: "capitalize",
        fontWeight: "500",
        cursor: "pointer",
        backgroundColor: match ? "primary.main" : "grey.300",
        color: match ? "white" : "grey.700",
        "&:hover": {
          backgroundColor: match ? "primary.main" : "grey.400",
          color: match ? "white" : "grey.700",
        },
      }}
    />
  )
}

export function PageTitle({ title, breadcrumbs, tabs, padding, border, rightBlock }: PageTitleProps) {
  return (
    <Box
      sx={{
        bgColor: "grey.300",
        padding: padding ? 3 : 0,
        borderBottom: border ? 1 : 0,
        borderColor: "grey.300",
        pb: 3,
        display: "flex",
        justifyContent: "space-between",
        alignItems: 'end'
      }}
    >
      <Box>
      <Breadcrumbs separator="›" sx={{}}>
        {breadcrumbs?.map((breadcrumb, index) => (
          <TBreadcrumb key={index} {...breadcrumb} />
        ))}
      </Breadcrumbs>

      <Typography variant="h4" sx={{ fontWeight: "500" }}>
        {title ? ellipsisText(title, 100) : <span>&nbsp;</span>}
      </Typography>

      {tabs && (
        <Stack direction="row" overflow={"auto"} spacing={2} sx={{ mt: 1 }}>
          {tabs.map((tab, index) => {
            return <TChip key={index} {...tab} />
          })}
        </Stack>
      )}
      </Box>
      {rightBlock && <Box sx={{ mt: 2 }}>{rightBlock}</Box>}
    </Box>
  )
}
